import { useCallback, useMemo, useState } from 'react';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDashboardPeriodOptions } from '~/app/hooks/dashboard/useDashboardPeriodOptions';
import { useAuth } from '~/app/auth/context/AuthContext';
import { defaultDashboardPeriod } from '~/app/services/organization-manager-dashboard/organization-manager-dashboard.period';
import { OrganizationManagerDashboardService } from '~/app/services/organization-manager-dashboard/organization-manager-dashboard.service';
import type { DashboardPeriod } from '~/app/services/organization-manager-dashboard/organization-manager-dashboard.types';
import type { OrganizationManagerDashboardPageViewProps } from '../OrganizationManagerDashboardPage.types';
import {
  firstNameFromDisplayName,
  isInitialDataLoad,
  mapPortfolioRows,
  resolveGreetingPeriod,
} from '../utils';
import { usePortfolioTableColumns } from './usePortfolioTableColumns';

export function useOrganizationManagerDashboardPage(): OrganizationManagerDashboardPageViewProps {
  const { t } = useTranslation('dashboard');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const organizationId = user?.organizationId ?? '';
  const columns = usePortfolioTableColumns();
  const periodOptions = useDashboardPeriodOptions();
  const [period, setPeriod] = useState<DashboardPeriod>(() =>
    defaultDashboardPeriod(),
  );

  const statsQuery = useQuery({
    queryKey: [
      'organization-manager-dashboard',
      'stats',
      organizationId,
      period,
    ],
    queryFn: () =>
      OrganizationManagerDashboardService.getStats(organizationId, period),
    enabled: !!organizationId,
    placeholderData: keepPreviousData,
  });

  const stats = statsQuery.data;
  const hasOrganization = !!organizationId;

  const firstName = firstNameFromDisplayName(user?.name) || 'Guillaume';
  const greetingPeriod = resolveGreetingPeriod(new Date().getHours());

  const greeting = t(`header.greeting.${greetingPeriod}`, { name: firstName });
  const subtitle = t('header.subtitle', {
    sites: stats?.siteCount ?? 0,
    chargers: stats?.chargerCount ?? 0,
  });
  const portfolioRows = useMemo(
    () => (stats ? mapPortfolioRows(stats) : []),
    [stats],
  );

  const onPeriodChange = useCallback((next: DashboardPeriod) => {
    setPeriod(next);
  }, []);

  const onRefresh = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ['organization-manager-dashboard'],
    });
    void queryClient.invalidateQueries({ queryKey: ['charging-kpis'] });
    void queryClient.invalidateQueries({ queryKey: ['operations-kpis'] });
  }, [queryClient]);

  const onViewDetails = useCallback((_sectionId: string) => {
    // Drill-down routes land in a follow-up ticket.
  }, []);

  const isRefreshing =
    queryClient.isFetching({
      queryKey: ['organization-manager-dashboard', 'stats', organizationId],
    }) > 0 ||
    queryClient.isFetching({ queryKey: ['charging-kpis', organizationId] }) >
      0 ||
    queryClient.isFetching({ queryKey: ['operations-kpis', organizationId] }) >
      0;

  return {
    isLoading:
      hasOrganization &&
      isInitialDataLoad(statsQuery.isPending, statsQuery.data),
    isError: !hasOrganization || statsQuery.isError,
    errorTitle: t('errors.loadTitle'),
    errorMessage: !hasOrganization
      ? t('errors.missingOrganization')
      : statsQuery.error instanceof Error
        ? statsQuery.error.message
        : t('errors.loadFallback'),
    greeting,
    subtitle,
    period,
    periodOptions,
    periodAriaLabel: t('header.period.ariaLabel'),
    refreshAriaLabel: t('actions.refresh'),
    refreshLoading: isRefreshing,
    viewDetailsLabel: t('actions.viewDetails'),
    accountabilityTitle: t('accountability.title'),
    accountabilitySubtitle: t('accountability.subtitle'),
    portfolioTitle: t('portfolio.title'),
    portfolioColumns: columns,
    portfolioRows,
    portfolioRefreshing: statsQuery.isFetching && statsQuery.isPlaceholderData,
    onPeriodChange,
    onRefresh,
    onViewDetails,
  };
}
